import levelsData from "../../generated/levels.json" with { type: "json" };
import { FRAME_RATE, STAGE_HEIGHT, STAGE_WIDTH } from "../core/constants.js";
import { ClockDisp } from "../core/clock-disp.js";
import { InputStatusDisp } from "../core/input-status-disp.js";
import { PHYS_SCALE } from "./box2d-compat.js";
import { LevelRuntime } from "./level-runtime.js";
import { LevelOneRuntime } from "./level-one-runtime.js";
import { LevelFourRuntime } from "./level-four-runtime.js";
import { LevelFiveRuntime } from "./level-five-runtime.js";
import { LevelSixRuntime } from "./level-six-runtime.js";
import { LevelSevenRuntime } from "./level-seven-runtime.js";
import { LevelEightRuntime } from "./level-eight-runtime.js";
import { LevelElevenRuntime } from "./level-eleven-runtime.js";
import { LevelFifteenRuntime } from "./level-fifteen-runtime.js";
import { LevelFourteenRuntime } from "./level-fourteen-runtime.js";
import { LevelNineRuntime } from "./level-nine-runtime.js";
import { LevelTenRuntime } from "./level-ten-runtime.js";
import { LevelThirteenRuntime } from "./level-thirteen-runtime.js";
import { LevelTwelveRuntime } from "./level-twelve-runtime.js";
import { LevelTwoRuntime } from "./level-two-runtime.js";
import { LevelThreeRuntime } from "./level-three-runtime.js";
import { LevelSixteenRuntime } from "./level-sixteen-runtime.js";
import { LevelSeventeenRuntime } from "./level-seventeen-runtime.js";

const LAST_LEVEL_ID = 17;
const LEFT_KEYS = ["ArrowLeft", "KeyA"];
const RIGHT_KEYS = ["ArrowRight", "KeyD"];
const UP_KEYS = ["ArrowUp", "KeyW", "Space"];
const DOWN_KEYS = ["ArrowDown", "KeyS"];
const FREE_CAMERA_PAN_SPEED = 8 * FRAME_RATE;

function getPlayerHitboxHalfExtent(player) {
  const size = player.radius * 2;
  return size / 2 * (Math.abs(Math.cos(player.body.GetAngle())) + Math.abs(Math.sin(player.body.GetAngle())));
}

export class GameSession {
  constructor({ canvas, hudLayer, store, input, showFinishHitbox = false, showBallHitbox = false, autoAdvanceLevel = true, spikesKill = false, fixedLevelTransitionTiming = false, tasDeathWarp = false, onExit, clock = null, attachClock = true, audioEnabled = true, respawnOnFailure = true }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext("2d");
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = "high";
    this.hudLayer = hudLayer;
    this.store = store;
    this.input = input;
    this.showFinishHitbox = showFinishHitbox;
    this.showBallHitbox = showBallHitbox;
    this.autoAdvanceLevel = autoAdvanceLevel;
    this.spikesKill = spikesKill;
    this.fixedLevelTransitionTiming = fixedLevelTransitionTiming;
    this.tasDeathWarp = tasDeathWarp;
    this.onExit = onExit;
    this.respawnOnFailure = respawnOnFailure;
    this.runtime = null;
    this.paused = false;
    this.levelId = 1;
    this.pauseCard = null;
    this.audioEnabled = audioEnabled;
    this.clock = clock ?? ClockDisp.getInstance();
    this.inputStatusDisp = new InputStatusDisp();
    this.zoom = 1;
    this.hitboxStrokeWidth = 2;
    this.freeCameraEnabled = false;
    this.freeCamera = { x: 0, y: 0 };
    this.manualFreeCameraInput = {
      isDown: (code) => this.input.isManualDown?.(code) ?? this.input.isDown(code),
    };
    if (attachClock) {
      this.clock.attach(hudLayer, 24, 20);
    }
    const inputStatusX = STAGE_WIDTH - this.inputStatusDisp.width - 24;
    this.inputStatusDisp.attach(hudLayer, inputStatusX, 20);
    this.updateInputStatusDisplay();
  }

  clampZoom(value) {
    return Math.min(4, Math.max(0.1, Number(value) || 1));
  }

  applyViewportTransform(ctx) {
    ctx.translate(STAGE_WIDTH / 2, STAGE_HEIGHT / 2);
    ctx.scale(this.zoom, this.zoom);
    ctx.translate(-STAGE_WIDTH / 2, -STAGE_HEIGHT / 2);
  }

  setZoom(value) {
    this.zoom = this.clampZoom(value);
  }

  setHitboxStrokeWidth(value) {
    this.hitboxStrokeWidth = Math.min(2, Math.max(0.1, Number(value) || 2));
  }

  captureFreeCamera() {
    if (!this.runtime?.camera) {
      return;
    }
    this.freeCamera.x = this.runtime.camera.x;
    this.freeCamera.y = this.runtime.camera.y;
  }

  centerCameraOnPlayer() {
    if (!this.runtime?.camera || !this.runtime?.player) {
      return;
    }
    this.runtime.camera.x = 275 - this.runtime.player.x;
    this.runtime.camera.y = 200 - this.runtime.player.y;
  }

  applyFreeCamera() {
    if (!this.freeCameraEnabled || !this.runtime?.camera) {
      return;
    }
    this.runtime.camera.x = this.freeCamera.x;
    this.runtime.camera.y = this.freeCamera.y;
  }

  setFreeCameraEnabled(enabled) {
    this.freeCameraEnabled = enabled === true;
    if (this.freeCameraEnabled) {
      this.captureFreeCamera();
      this.applyFreeCamera();
      return;
    }
    this.centerCameraOnPlayer();
  }

  updateFreeCamera(deltaMs = 1000 / FRAME_RATE) {
    if (!this.freeCameraEnabled || !this.runtime?.camera) {
      return;
    }
    const deltaSeconds = Math.max(0, deltaMs) / 1000;
    const panSpeed = FREE_CAMERA_PAN_SPEED * deltaSeconds / this.zoom;
    if (LEFT_KEYS.some((code) => this.manualFreeCameraInput.isDown(code))) {
      this.freeCamera.x += panSpeed;
    }
    if (RIGHT_KEYS.some((code) => this.manualFreeCameraInput.isDown(code))) {
      this.freeCamera.x -= panSpeed;
    }
    if (UP_KEYS.some((code) => this.manualFreeCameraInput.isDown(code))) {
      this.freeCamera.y += panSpeed;
    }
    if (DOWN_KEYS.some((code) => this.manualFreeCameraInput.isDown(code))) {
      this.freeCamera.y -= panSpeed;
    }
    this.applyFreeCamera();
  }

  createRuntime(levelId, levelData, runtimeState) {
    let runtime;
    switch (levelId) {
      case 1:
        runtime = new LevelOneRuntime(levelData, this.clock, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 2:
        runtime = new LevelTwoRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 3:
        runtime = new LevelThreeRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 4:
        runtime = new LevelFourRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 5:
        runtime = new LevelFiveRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 6:
        runtime = new LevelSixRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 7:
        runtime = new LevelSevenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 8:
        runtime = new LevelEightRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 9:
        runtime = new LevelNineRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 10:
        runtime = new LevelTenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 11:
        runtime = new LevelElevenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 12:
        runtime = new LevelTwelveRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 13:
        runtime = new LevelThirteenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 14:
        runtime = new LevelFourteenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 15:
        runtime = new LevelFifteenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 16:
        runtime = new LevelSixteenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      case 17:
        runtime = new LevelSeventeenRuntime(levelData, this.clock, runtimeState, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
      default:
        runtime = new LevelRuntime(levelData, this.clock, {
          audioEnabled: this.audioEnabled,
          fixedLevelTransitionTiming: this.fixedLevelTransitionTiming,
          tasDeathWarp: this.tasDeathWarp,
        });
        break;
    }
    runtime.setSpikesKill?.(this.spikesKill);
    runtime.setFixedLevelTransitionTiming?.(this.fixedLevelTransitionTiming);
    runtime.setTasDeathWarp?.(this.tasDeathWarp);
    return runtime;
  }

  startLevel(levelId, options = {}) {
    const { preserveClock = false, runtimeState = null, clockState = null } = options;
    this.levelId = levelId;
    this.hidePauseCard();
    const levelData = levelsData.levels.find((entry) => entry.id === levelId);
    this.runtime = this.createRuntime(levelId, levelData, runtimeState);
    if (preserveClock && clockState) {
      this.clock.setState(clockState);
      this.clock.unpause();
    } else {
      this.clock.reset();
      this.clock.unpause();
    }
    if (!preserveClock) {
      this.input.resetRace?.();
    }
    this.paused = false;
    if (this.freeCameraEnabled) {
      this.applyFreeCamera();
    } else {
      this.centerCameraOnPlayer();
    }
  }

  restartLevelFromStart() {
    this.startLevel(this.levelId);
  }

  respawnAtCheckpoint() {
    this.startLevel(this.levelId, {
      preserveClock: true,
      runtimeState: this.runtime?.serializeState?.() ?? null,
      clockState: this.clock.getState(),
    });
  }

  getRaceFrame() {
    return this.clock.frames;
  }

  getTelemetry() {
    const player = this.runtime?.player;
    const livePlayer = player?.isLive?.() ? player : null;
    const velocity = livePlayer ? livePlayer.body.GetLinearVelocity() : null;
    const angularVelocity = livePlayer ? livePlayer.body.GetAngularVelocity() : null;
    const position = livePlayer ? livePlayer.body.GetPosition() : null;
    const angle = livePlayer ? livePlayer.body.GetAngle() : null;
    const hitboxHalfExtent = livePlayer ? getPlayerHitboxHalfExtent(livePlayer) : 0;
    const frame = this.clock.frames;
    const x = livePlayer ? position.x * PHYS_SCALE : 0;
    const y = livePlayer ? position.y * PHYS_SCALE : 0;
    return {
      frame,
      frameTime: frame / FRAME_RATE,
      vx: velocity?.x ?? 0,
      vy: velocity?.y ?? 0,
      rotationVelocity: angularVelocity ? angularVelocity * (180 / Math.PI) : 0,
      x,
      y,
      rotation: livePlayer ? (angle * (180 / Math.PI)) % 360 : 0,
      hitboxRightX: x + hitboxHalfExtent,
      hitboxLeftX: x - hitboxHalfExtent,
      hitboxTopY: y - hitboxHalfExtent,
      hitboxBottomY: y + hitboxHalfExtent,
    };
  }

  togglePause() {
    if (this.runtime?.complete && !this.autoAdvanceLevel) {
      return;
    }
    if (this.paused) {
      this.resume();
      return;
    }
    this.pause();
  }

  pause() {
    this.paused = true;
    this.clock.Pause();
    this.showPauseCard();
  }

  resume() {
    this.paused = false;
    this.clock.unpause();
    this.hidePauseCard();
  }

  isPaused() {
    return this.paused;
  }

  showPauseCard() {
    this.hidePauseCard();
    const card = document.createElement("div");
    card.className = "pause-card";
    card.innerHTML = "<strong>Paused</strong><div class='legend'>Press P to resume, F to checkpoint, R to restart, Esc to menu.</div>";
    this.hudLayer.appendChild(card);
    this.pauseCard = card;
  }

  showLevelCompleteCard() {
    this.hidePauseCard();
    const card = document.createElement("div");
    card.className = "pause-card";

    const title = document.createElement("strong");
    title.textContent = this.levelId < LAST_LEVEL_ID ? "Level Complete" : "Game Complete";

    const legend = document.createElement("div");
    legend.className = "legend";
    legend.textContent = this.levelId < LAST_LEVEL_ID
      ? "Auto-next is off. Continue when ready, or press Esc for level select."
      : "Final level cleared. Press Esc for level select or R to replay.";

    card.append(title, legend);
    if (this.levelId < LAST_LEVEL_ID) {
      const actions = document.createElement("div");
      actions.className = "pause-actions";

      const nextButton = document.createElement("button");
      nextButton.type = "button";
      nextButton.className = "btn btn-small";
      nextButton.textContent = "Next Level";
      nextButton.addEventListener("click", () => this.advanceCompletedLevel());

      actions.appendChild(nextButton);
      card.appendChild(actions);
    }
    this.hudLayer.appendChild(card);
    this.pauseCard = card;
  }

  hidePauseCard() {
    this.pauseCard?.remove();
    this.pauseCard = null;
  }

  setShowFinishHitbox(enabled) {
    this.showFinishHitbox = enabled;
  }

  setShowBallHitbox(enabled) {
    this.showBallHitbox = enabled;
  }

  setAutoAdvanceLevel(enabled) {
    this.autoAdvanceLevel = enabled;
  }

  setSpikesKill(enabled) {
    this.spikesKill = enabled;
    this.runtime?.setSpikesKill?.(enabled);
  }

  setFixedLevelTransitionTiming(enabled) {
    this.fixedLevelTransitionTiming = enabled;
    this.runtime?.setFixedLevelTransitionTiming?.(enabled);
  }

  setTasDeathWarp(enabled) {
    this.tasDeathWarp = enabled;
    this.runtime?.setTasDeathWarp?.(enabled);
  }

  advanceCompletedLevel() {
    if (this.levelId < LAST_LEVEL_ID) {
      this.startLevel(this.levelId + 1);
      return;
    }
    this.hidePauseCard();
    this.clock.Pause();
    this.onExit();
  }

  renderFinishHitbox() {
    if (!this.showFinishHitbox || !this.runtime?.goalRect) {
      return;
    }
    const { goalRect, camera } = this.runtime;
    this.ctx.save();
    this.applyViewportTransform(this.ctx);
    this.ctx.translate(camera?.x ?? 0, camera?.y ?? 0);
    this.ctx.fillStyle = "rgba(255, 214, 10, 0.28)";
    this.ctx.strokeStyle = "rgba(240, 177, 0, 0.95)";
    this.ctx.lineWidth = this.hitboxStrokeWidth;
    this.ctx.fillRect(goalRect.x, goalRect.y, goalRect.width, goalRect.height);
    this.ctx.strokeRect(goalRect.x, goalRect.y, goalRect.width, goalRect.height);
    this.ctx.restore();
  }

  renderBallHitbox() {
    const player = this.runtime?.player;
    if (!this.showBallHitbox || !player?.isLive?.()) {
      return;
    }
    const camera = this.runtime?.camera;
    const halfExtent = getPlayerHitboxHalfExtent(player);
    const position = player.body.GetPosition();
    const supportChecks = [
      { x: position.x, y: position.y + 0.4 },
      { x: position.x - 0.2, y: position.y + 0.38 },
      { x: position.x + 0.2, y: position.y + 0.38 },
    ];
    const hasGroundSupport = supportChecks.some((point) => Boolean(this.runtime?.getBodyAtPoint?.(point.x, point.y, true)));
    const jumpPossible = (this.runtime?.contactListener?.playerContactBodies?.length ?? 0) > 0 && hasGroundSupport;
    const supportFillStyle = jumpPossible ? "rgba(46, 160, 67, 0.28)" : "rgba(211, 47, 47, 0.18)";
    const supportStrokeStyle = jumpPossible ? "rgba(46, 160, 67, 0.95)" : "rgba(211, 47, 47, 0.95)";
    this.ctx.save();
    this.applyViewportTransform(this.ctx);
    this.ctx.translate(camera?.x ?? 0, camera?.y ?? 0);
    this.ctx.fillStyle = "rgba(211, 47, 47, 0.18)";
    this.ctx.strokeStyle = "rgba(211, 47, 47, 0.95)";
    this.ctx.lineWidth = this.hitboxStrokeWidth;
    this.ctx.beginPath();
    this.ctx.arc(player.x, player.y, player.radius, 0, Math.PI * 2);
    this.ctx.fill();
    this.ctx.stroke();
    this.ctx.strokeRect(player.x - halfExtent, player.y - halfExtent, halfExtent * 2, halfExtent * 2);
    this.ctx.fillStyle = supportFillStyle;
    this.ctx.strokeStyle = supportStrokeStyle;
    for (const point of supportChecks) {
      this.ctx.beginPath();
      this.ctx.arc(point.x * PHYS_SCALE, point.y * PHYS_SCALE, 3, 0, Math.PI * 2);
      this.ctx.fill();
      this.ctx.stroke();
    }
    this.ctx.restore();
  }

  updateRuntimeFrame(input = this.input) {
    if (!this.runtime) {
      return;
    }
    this.runtime.update(input);
    if (this.runtime.complete) {
      if (this.levelId < LAST_LEVEL_ID) {
        this.store.unlock(this.levelId + 1);
      }
      if (this.autoAdvanceLevel) {
        this.advanceCompletedLevel();
      } else {
        this.paused = true;
        this.clock.Pause();
        this.showLevelCompleteCard();
      }
      return;
    }
    if (this.runtime.failed) {
      if (this.respawnOnFailure) {
        this.respawnAtCheckpoint();
      } else {
        this.paused = true;
        this.clock.Pause();
      }
    }
  }

  updateInputStatusDisplay() {
    const input = this.input;
    this.inputStatusDisp.update({
      upPressed: UP_KEYS.some((code) => input.isDown(code)),
      leftPressed: LEFT_KEYS.some((code) => input.isDown(code)),
      rightPressed: RIGHT_KEYS.some((code) => input.isDown(code)),
    });
  }

  stepFrame() {
    this.updateInputStatusDisplay();
    if (!this.runtime || this.paused) {
      return;
    }
    this.updateRuntimeFrame(this.input);
  }

  update() {
    this.updateInputStatusDisplay();
    if (this.input.wasPressed("KeyP")) {
      this.togglePause();
    }
    if (this.input.wasPressed("KeyR")) {
      this.restartLevelFromStart();
      return;
    }
    if (this.input.wasPressed("KeyF")) {
      this.respawnAtCheckpoint();
      return;
    }
    if (this.input.wasPressed("Escape")) {
      this.hidePauseCard();
      this.clock.Pause();
      this.onExit();
      return;
    }
    if (this.paused || !this.runtime) {
      return;
    }
    this.updateRuntimeFrame(this.input);
    this.applyFreeCamera();
  }

  render() {
    const scaleX = this.canvas.width / STAGE_WIDTH;
    const scaleY = this.canvas.height / STAGE_HEIGHT;
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (!this.runtime) {
      return;
    }
    this.ctx.setTransform(scaleX, 0, 0, scaleY, 0, 0);
    this.runtime.render(this.ctx, STAGE_WIDTH, STAGE_HEIGHT, { zoom: this.zoom });
    this.renderFinishHitbox();
    this.renderBallHitbox();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
}
